import { useCallback, useEffect, useState } from 'react';
import { groupBy, sortBy, sumBy, uniqBy } from 'lodash';
import { SecurityModel } from 'api';
import { OPTION, WARRANT } from 'common/constants/securityTypes';
import { CapTableInfo } from 'common/types/capTable';
import { CaptableColumn } from 'pages/CapTable/cap-table/components/captable-table/CaptableTable.props';
import { securityTemplate } from 'pages/CapTable/cap-table/data';

/**
 * Custom hook to compute virtual columns for securities that derive from the same security group.
 * A virtual column is a representation of a group of option or warrant securities, and as such is not persisted.
 * A single option or warrant security (one row in the ledger) does not a get virtual column representation.
 *
 * @param columns - The current list of cap table columns
 * @param captableInfo - The current cap table information
 * @returns Object containing virtualColumns array
 * */

const useVirtualColumns = ({ columns, captableInfo }: { columns: CaptableColumn[]; captableInfo: CapTableInfo }) => {
  const [virtualColumns, setVirtualColumns] = useState<CaptableColumn[]>([]);
  const createVirtualColumns = useCallback(
    (cols: CaptableColumn[]): CaptableColumn[] => {
      const sortedColumns = sortBy(cols, 'created_at');
      // security_group_ref identifies the securities that derive from the same column
      const uniqueSecurityGroupRefs: Partial<SecurityModel>[] = uniqBy(
        sortedColumns
          .filter(s => s.security_group_ref && s.id && [WARRANT, OPTION].includes(s.security_type))
          .map(s => ({
            // security_ref in the virtual columns is the security_group_ref of its hidden securities
            security_ref: s.security_group_ref,
            security_group_ref: s.security_group_ref,
            // Build the name of the virtual column based on the security:
            // "$2 Common Warrant" --> "Common Warrant"
            name: s.name.substr(s.name.indexOf(' ') + 1),
            security_type: s.security_type,
            underlying_security_ref: s.underlying_security_ref,
            underlying_security: s.underlying_security,
            order: s.order,
          })),
        'security_group_ref'
      );

      const calculateStrikePrice = (hiddenCols: CaptableColumn[]) => {
        let sharesByStrikePrices = 0;
        let totalShares = 0;

        hiddenCols.forEach(({ shares_outstanding, strike_price }) => {
          // Get the total of shares
          totalShares += Number(shares_outstanding);
          // Multiply each strike price by the number of associated shares
          sharesByStrikePrices += Number(shares_outstanding) * Number(strike_price);
        });
        return sharesByStrikePrices / totalShares;
      };

      if (uniqueSecurityGroupRefs.length > 0) {
        return uniqueSecurityGroupRefs.map(groupRef => {
          const securitiesToGroup = groupBy(sortedColumns, 'security_group_ref')[groupRef.security_group_ref!];
          const isUsedInFod = securitiesToGroup.some(security => security.fod_usage_details?.is_used_in_fod);
          const isUsedInBacksolve = securitiesToGroup.some(
            security => security.backsolve_usage_details?.is_used_in_backsolve
          );
          return {
            ...securityTemplate,
            id: null, // this identifies a virtual column
            security_ref: groupRef.security_ref,
            columnRef: groupRef.security_ref,
            cap_table: captableInfo?.id,
            name: groupRef.name!,
            shares_outstanding: sumBy(securitiesToGroup, security => Number(security.shares_outstanding)),
            security_type: groupRef.security_type!,
            underlying_security: groupRef.underlying_security,
            underlying_security_ref: groupRef.underlying_security_ref,
            shares_diluted: sumBy(securitiesToGroup, security => Number(security.shares_diluted)),
            strike_price: calculateStrikePrice(securitiesToGroup),
            options_ledger: securitiesToGroup.map(col => ({
              shares: col.shares_outstanding,
              price: col.strike_price,
              security_ref: col.security_ref,
            })),
            order: groupRef.order,
            security_group_ref: groupRef.security_group_ref,
            fod_usage_details: {
              is_used_in_fod: isUsedInFod,
              location: securitiesToGroup.flatMap(security => security.fod_usage_details?.location ?? []),
            },
            backsolve_usage_details: {
              is_used_in_backsolve: isUsedInBacksolve,
              basket_member_ids: securitiesToGroup.flatMap(
                security => security.backsolve_usage_details?.basket_member_ids ?? []
              ),
              location: securitiesToGroup.flatMap(security => security.backsolve_usage_details?.location ?? []),
            },
          } as unknown as CaptableColumn;
        });
      }
      return [];
    },
    [captableInfo?.id]
  );

  useEffect(() => {
    if (columns?.length) {
      setVirtualColumns(createVirtualColumns(columns));
    } else {
      setVirtualColumns([]);
    }
  }, [columns, createVirtualColumns]);

  return {
    virtualColumns,
  };
};

export default useVirtualColumns;
